import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type {
  Contract, SupportExtension, SupportExtensionCandidate, SupportExtensionPreview, SupportExtensionWrite,
} from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'
import { Card, EmptyState, ErrorBanner, Spinner } from './common'
import { DataTable, type Column } from './DataTable'
import { Finestra } from './ModificheGitlab'

/**
 * The extended support a contract buys (Microsoft ESU, RHEL ELS, Oracle
 * Extended Support…): which software or system, until when, on which
 * machines. While it holds, the end-of-support finding of those machines is
 * «supporto esteso» — not out of support, not an accepted risk — and it is out
 * of support again on its own when the extension or the contract ends. Every
 * write goes through a preview that says which findings and machines it covers.
 * See support_extension_service.
 */

export function dataBreve(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString(localeCorrente())
}

function etichettaAmbito(scope: SupportExtension['scope']): string {
  switch (scope) {
    case 'assets': return t('I beni coperti dal contratto')
    case 'cis': return t('CI scelti')
    case 'all': return t('Tutti i CI con questo prodotto')
  }
}

export function BadgeEstensione({ e }: { e: Pick<SupportExtension, 'in_force' | 'expiring'> }) {
  if (!e.in_force) return <span className="badge badge--muted">{t('non in vigore')}</span>
  if (e.expiring) return <span className="badge badge--warn">{t('in scadenza')}</span>
  return <span className="badge badge--ok">{t('in vigore')}</span>
}

/** The «Supporto esteso» tab of a contract. */
export function EstensioniSupporto({ contratto, puoModificare }: { contratto: Contract; puoModificare: boolean }) {
  const elenco = useAsync(() => api.supportExtensions(contratto.id), [contratto.id])
  const [modulo, setModulo] = useState<SupportExtension | 'nuova' | null>(null)
  const [errore, setErrore] = useState<unknown>(null)

  async function elimina(e: SupportExtension) {
    if (!window.confirm(t('Eliminare l’estensione «{nome}»? I reperti che copre tornano subito fuori supporto.', { nome: e.label }))) return
    setErrore(null)
    try {
      await api.deleteSupportExtension(contratto.id, e.id)
      await elenco.reload()
    } catch (err) {
      setErrore(err)
    }
  }

  const colonne: Column<SupportExtension>[] = [
    { key: 'label', header: t('Prodotto'), render: (e) => (
      <>
        <strong>{e.label}</strong>
        <div className="text-muted eagle-piccolo">
          {e.kind === 'system' ? t('Sistema operativo') : t('Software')}{e.cycle ? ` · ${t('ciclo {c}', { c: e.cycle })}` : ''}
        </div>
      </>
    ), value: (e) => `${e.label} ${e.cycle}` },
    { key: 'effective_until', header: t('Fino al'), mono: true, value: (e) => e.effective_until ?? e.until,
      render: (e) => (
        <>
          {dataBreve(e.effective_until ?? e.until)}
          {e.effective_until && e.effective_until !== e.until && (
            <div className="text-muted eagle-piccolo">{t('scadenza del contratto (estensione al {d})', { d: dataBreve(e.until) })}</div>
          )}
        </>
      ) },
    { key: 'in_force', header: t('Stato'), value: (e) => (e.in_force ? (e.expiring ? 'expiring' : 'in_force') : 'off'),
      render: (e) => <BadgeEstensione e={e} /> },
    { key: 'scope', header: t('Su quali macchine'), value: (e) => etichettaAmbito(e.scope),
      render: (e) => (
        <>
          {etichettaAmbito(e.scope)}
          {e.scope === 'cis' && <div className="text-muted eagle-piccolo">{t('{n} CI', { n: e.ci_ids.length })}</div>}
        </>
      ) },
    { key: 'covered_cis', header: t('CI coperti oggi'), value: (e) => e.covered_cis },
    ...(puoModificare ? [{
      key: 'azioni', header: '', sort: false, searchable: false,
      render: (e: SupportExtension) => (
        <span className="estensione-azioni">
          <button type="button" className="secondary" onClick={() => setModulo(e)}>{t('Modifica')}</button>
          <button type="button" className="secondary" onClick={() => void elimina(e)}>{t('Elimina')}</button>
        </span>
      ),
    } as Column<SupportExtension>] : []),
  ]

  return (
    <Card title={t('Supporto esteso')}
          actions={puoModificare && !modulo ? <button type="button" onClick={() => setModulo('nuova')}>{t('Aggiungi un’estensione')}</button> : undefined}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Un contratto che estende il supporto oltre la data del produttore (ESU di Microsoft, ELS di Red Hat, Extended Support di Oracle…): finché vale, le macchine coperte non risultano fuori supporto ma «supporto esteso fino al …», con il contratto citato; alla fine dell’estensione, o del contratto, tornano fuori supporto da sole.')}
      </p>
      <ErrorBanner error={errore ?? elenco.error} />
      {elenco.loading && !elenco.data ? <Spinner /> : (elenco.data ?? []).length === 0 ? (
        <EmptyState title={t('Nessuna estensione')} hint={t('Aggiungi il software o il sistema che questo contratto mantiene supportato.')} />
      ) : (
        <DataTable rows={elenco.data ?? []} columns={colonne} rowKey={(e) => e.id} conteggio={{ nome: t('estensioni') }} />
      )}
      {modulo && (
        <ModuloEstensione
          contrattoFisso={contratto}
          esistente={modulo === 'nuova' ? null : modulo}
          onAnnulla={() => setModulo(null)}
          onFatto={async () => { setModulo(null); await elenco.reload() }}
        />
      )}
    </Card>
  )
}

/** What a new extension starts from: a group of findings, or nothing. */
export interface Partenza {
  kind: 'software' | 'system'
  product: string
  cycle: string
  label: string
  ci: { id: string; name: string }[]
  extended_support?: string | null
}

function chiaveCandidato(c: Pick<Partenza, 'kind' | 'product' | 'cycle' | 'label'>): string {
  return `${c.kind}|${c.product}|${c.cycle}|${c.label}`
}

/**
 * The two-step form: what, until when, on which machines → the preview from
 * the server (findings and CIs it covers, warnings) → save. With a fixed
 * contract (the contract's tab) or with a contract to choose («Copri con un
 * contratto» from the end-of-support list).
 */
export function ModuloEstensione({ contrattoFisso, esistente, partenza, onAnnulla, onFatto }: {
  contrattoFisso?: Contract
  esistente?: SupportExtension | null
  partenza?: Partenza
  onAnnulla: () => void
  onFatto: () => void | Promise<void>
}) {
  const candidati = useAsync(() => api.supportExtensionCandidates(), [])
  const contratti = useAsync<Contract[]>(() => contrattoFisso ? Promise.resolve([]) : api.contracts({ status: 'active' }), [contrattoFisso?.id])
  const [contrattoId, setContrattoId] = useState(contrattoFisso?.id ?? '')
  const [v, setV] = useState<SupportExtensionWrite>(() => esistente
    ? { kind: esistente.kind, product: esistente.product, cycle: esistente.cycle, label: esistente.label,
        until: esistente.until, scope: esistente.scope, ci_ids: esistente.ci_ids, notes: esistente.notes }
    : { kind: partenza?.kind ?? 'system', product: partenza?.product ?? '', cycle: partenza?.cycle ?? '',
        label: partenza?.label ?? '', until: partenza?.extended_support || contrattoFisso?.end_date || '',
        scope: partenza ? 'cis' : 'assets', ci_ids: partenza?.ci.map((c) => c.id) ?? [], notes: '' })
  const [anteprima, setAnteprima] = useState<SupportExtensionPreview | null>(null)
  const [busy, setBusy] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)

  const scelto = useMemo(() => {
    const tutti: (SupportExtensionCandidate | Partenza)[] = [...(candidati.data ?? [])]
    return tutti.find((c) => chiaveCandidato(c) === chiaveCandidato(v)) ?? partenza ?? null
  }, [candidati.data, v, partenza])
  const ciScegli = scelto?.ci ?? []
  const contratto = contrattoFisso ?? (contratti.data ?? []).find((c) => c.id === contrattoId)

  function cambia(patch: Partial<SupportExtensionWrite>) {
    setV((x) => ({ ...x, ...patch }))
    setAnteprima(null)
  }

  function scegliCandidato(chiave: string) {
    const c = (candidati.data ?? []).find((x) => chiaveCandidato(x) === chiave)
    if (!c) return
    cambia({
      kind: c.kind, product: c.product, cycle: c.cycle, label: c.label,
      until: c.extended_support || v.until || contratto?.end_date || '',
      ci_ids: v.scope === 'cis' ? c.ci_ids : v.ci_ids,
    })
  }

  async function prova() {
    if (!contrattoId) return
    setBusy(true)
    setErrore(null)
    try {
      setAnteprima(await api.previewSupportExtension(contrattoId, v))
    } catch (err) {
      setErrore(err)
    } finally {
      setBusy(false)
    }
  }

  async function salva() {
    if (!contrattoId) return
    setBusy(true)
    setErrore(null)
    try {
      if (esistente) await api.updateSupportExtension(contrattoId, esistente.id, v)
      else await api.createSupportExtension(contrattoId, v)
      await onFatto()
    } catch (err) {
      setErrore(err)
    } finally {
      setBusy(false)
    }
  }

  const pronto = Boolean(contrattoId && v.until && (v.kind === 'system' ? (v.product || v.label) : v.product)
    && (v.scope !== 'cis' || v.ci_ids.length > 0))

  return (
    <Finestra titolo={esistente ? t('Modifica l’estensione') : t('Supporto esteso da contratto')}
              sotto={t('Prima l’anteprima: dice quali reperti e quali macchine copre. Niente è scritto finché non confermi.')}
              onChiudi={onAnnulla}>
      <ErrorBanner error={errore} />
      <div className="eagle-form contratto-form">
        {!contrattoFisso && (
          <label className="field"><span className="field__label">{t('Contratto')}</span>
            <select value={contrattoId} onChange={(e) => { setContrattoId(e.target.value); setAnteprima(null) }}>
              <option value="">{contratti.loading ? t('Caricamento…') : t('scegli un contratto in vigore')}</option>
              {(contratti.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>{c.reference} · {c.vendor}{c.name ? ` · ${c.name}` : ''}</option>
              ))}
            </select>
            {contratti.data && contratti.data.length === 0 && (
              <p className="field__hint">{t('Nessun contratto in vigore: registralo prima in Contratti (tipo «Supporto esteso»).')}</p>
            )}
          </label>
        )}

        {!esistente && !partenza && (
          <label className="field"><span className="field__label">{t('Dai reperti di fine supporto')}</span>
            <select value={scelto && candidati.data?.some((c) => chiaveCandidato(c) === chiaveCandidato(scelto)) ? chiaveCandidato(scelto) : ''}
                    onChange={(e) => scegliCandidato(e.target.value)}>
              <option value="">{candidati.loading ? t('Caricamento…') : t('scegli, oppure scrivi qui sotto')}</option>
              {(candidati.data ?? []).map((c) => (
                <option key={chiaveCandidato(c)} value={chiaveCandidato(c)}>
                  {c.label}{c.cycle ? ` ${c.cycle}` : ''} · {t('{n} CI', { n: c.cis })}{c.covered ? ` · ${t('{n} già coperti', { n: c.covered })}` : ''}
                </option>
              ))}
            </select>
            <p className="field__hint">{t('I prodotti e i sistemi che oggi risultano fuori supporto o in scadenza, con quante macchine.')}</p>
          </label>
        )}

        <div className="eagle-report__riga">
          <label className="field" style={{ maxWidth: 220 }}><span className="field__label">{t('Che cosa')}</span>
            <select value={v.kind} onChange={(e) => cambia({ kind: e.target.value as SupportExtensionWrite['kind'] })} disabled={Boolean(partenza)}>
              <option value="system">{t('Sistema operativo')}</option>
              <option value="software">{t('Software')}</option>
            </select></label>
          <label className="field"><span className="field__label">{t('Nome')}</span>
            <input value={v.label} onChange={(e) => cambia({ label: e.target.value })} placeholder={t('Windows Server 2012 R2')} /></label>
          {v.kind === 'software' && (
            <>
              <label className="field" style={{ maxWidth: 200 }}><span className="field__label">{t('Prodotto (chiave)')}</span>
                <input className="mono" value={v.product} onChange={(e) => cambia({ product: e.target.value })} disabled={Boolean(partenza)} /></label>
              <label className="field" style={{ maxWidth: 160 }}><span className="field__label">{t('Ciclo')}</span>
                <input className="mono" value={v.cycle} onChange={(e) => cambia({ cycle: e.target.value })} placeholder={t('tutti')} /></label>
            </>
          )}
          <label className="field" style={{ maxWidth: 190 }}><span className="field__label">{t('Supporto esteso fino al')}</span>
            <input type="date" value={v.until} onChange={(e) => cambia({ until: e.target.value })} />
            {scelto?.extended_support && (
              <p className="field__hint">{t('Il produttore offre supporto esteso fino al {d}.', { d: dataBreve(scelto.extended_support) })}</p>
            )}
          </label>
        </div>

        <fieldset className="field">
          <legend className="field__label">{t('Su quali macchine')}</legend>
          {(['assets', 'cis', 'all'] as const).map((s) => (
            <label key={s} className="eagle-interruttore" style={{ display: 'block' }}>
              <input type="radio" name="ambito" checked={v.scope === s}
                     onChange={() => cambia({ scope: s, ci_ids: s === 'cis' && v.ci_ids.length === 0 ? ciScegli.map((c) => c.id) : v.ci_ids })} />{' '}
              {etichettaAmbito(s)}
              <span className="muted"> — {s === 'assets'
                ? t('le macchine dei beni del contratto, con le date di copertura di ogni bene')
                : s === 'cis' ? t('per le macchine senza un bene registrato')
                : t('per un accordo a volume che vale per tutto il parco')}</span>
            </label>
          ))}
          {v.scope === 'cis' && (
            ciScegli.length === 0 ? (
              <p className="field__hint">{t('Scegli prima il prodotto dai reperti: le macchine sono quelle che lo hanno.')}</p>
            ) : (
              <div className="estensione-ci">
                <div className="estensione-azioni">
                  <button type="button" className="link" onClick={() => cambia({ ci_ids: ciScegli.map((c) => c.id) })}>{t('Tutte')}</button>
                  <button type="button" className="link" onClick={() => cambia({ ci_ids: [] })}>{t('Nessuna')}</button>
                  <span className="muted">{t('{n} di {tot} scelte', { n: v.ci_ids.length, tot: ciScegli.length })}</span>
                </div>
                <div className="estensione-ci__elenco">
                  {ciScegli.map((c) => (
                    <label key={c.id} className="eagle-interruttore">
                      <input type="checkbox" checked={v.ci_ids.includes(c.id)}
                             onChange={(e) => cambia({ ci_ids: e.target.checked ? [...v.ci_ids, c.id] : v.ci_ids.filter((x) => x !== c.id) })} />{' '}
                      {c.name || c.id}
                    </label>
                  ))}
                </div>
              </div>
            )
          )}
        </fieldset>

        <label className="field"><span className="field__label">{t('Note')}</span>
          <textarea rows={2} value={v.notes} onChange={(e) => cambia({ notes: e.target.value })}
                    placeholder={t('anno di ESU, codice d’attivazione dove si trova, chi lo rinnova…')} /></label>

        {anteprima && (
          <div className="estensione-anteprima" role="status">
            <strong>
              {anteprima.effective_until
                ? t('Coprirebbe {f} reperti su {n} CI, fino al {d}.', { f: anteprima.findings, n: anteprima.cis, d: dataBreve(anteprima.effective_until) })
                : t('Oggi non coprirebbe niente.')}
            </strong>
            {anteprima.ci_names.length > 0 && (
              <p className="field__hint">{anteprima.ci_names.join(', ')}{anteprima.cis > anteprima.ci_names.length ? ' …' : ''}</p>
            )}
            {anteprima.warnings.map((w) => <p key={w} className="field__hint estensione-anteprima__avviso">⚠ {w}</p>)}
            <p className="field__hint">{t('Niente è ancora stato scritto.')}</p>
          </div>
        )}

        <div className="eagle-form__azioni">
          {anteprima ? (
            <button type="button" onClick={() => void salva()} disabled={busy}>
              {busy ? t('Salvo…') : esistente ? t('Salva l’estensione') : t('Registra l’estensione')}
            </button>
          ) : (
            <button type="button" onClick={() => void prova()} disabled={busy || !pronto}>
              {busy ? t('Calcolo…') : t('Anteprima')}
            </button>
          )}
          {anteprima && <button type="button" className="secondary" onClick={() => setAnteprima(null)} disabled={busy}>{t('Indietro')}</button>}
          <button type="button" className="secondary" onClick={onAnnulla} disabled={busy}>{t('Annulla')}</button>
        </div>
        {contratto && (
          <p className="field__hint">
            {t('Contratto')}: <Link to={`/contracts/${contratto.id}`}>{contratto.reference}</Link> · {contratto.vendor}
            {contratto.end_date ? ` · ${t('scade il {d}', { d: dataBreve(contratto.end_date) })}` : ''}
          </p>
        )}
      </div>
    </Finestra>
  )
}
